import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { academicService } from '../services/academicService';
import { studentService } from '../services/studentService';
import AnnouncementsTab from '../components/AnnouncementsTab';
import GradesTab from '../components/GradesTab';
import AssignmentsTab from '../components/AssignmentsTab';
import AttendanceTab from '../components/AttendanceTab';
import AddStudentModal from '../components/AddStudentModal';
import EditStudentModal from '../components/EditStudentModal';

export default function TeacherDashboard() {
  const { levelCode } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('grades');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  const [classData, setClassData] = useState(null);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalAssignments: 0,
    pendingGrades: 0,
    attendanceRate: 0,
    recentActivity: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [studentStats, setStudentStats] = useState({});
  const [academicClass, setAcademicClass] = useState(null);

  useEffect(() => {
    const checkTeacherAccess = async () => {
      try {
        // Get user data from session storage
        const authData = sessionStorage.getItem('user_auth');
        if (!authData) {
          navigate('/');
          return;
        }

        const { user } = JSON.parse(authData);
        setUserData(user);

        // Check if user is a teacher or admin
        if (user.role !== 'instructor' && user.role !== 'admin') {
          setError('Only teachers and admins can access this page');
          setLoading(false);
          return;
        }

        // Check if teacher is assigned to this level and get class data
        // For admin users, skip the teacher_id check
        let teacherLevel, teacherLevelError;
        
        if (user.role === 'admin') {
          // Admin can access any level
          const { data, error } = await supabase
            .from('levels')
            .select('*')
            .eq('code', levelCode)
            .single();
          teacherLevel = data;
          teacherLevelError = error;
        } else {
          // Regular teacher must be assigned as primary or secondary teacher
          const { data, error } = await supabase
            .from('levels')
            .select('*')
            .eq('code', levelCode)
            .or(`teacher_id.eq.${user.id},secondary_teacher_id.eq.${user.id}`)
            .single();
          teacherLevel = data;
          teacherLevelError = error;
        }

        if (teacherLevelError || !teacherLevel) {
          setError('You are not authorized to access this level');
          setLoading(false);
          return;
        }

        setClassData(teacherLevel);
        const linkedClass = await academicService.getClassByLevelCode(levelCode);
        setAcademicClass(linkedClass);
        await fetchDashboardData();
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    checkTeacherAccess();
  }, [levelCode, navigate]);

  useEffect(() => {
    if (activeTab === 'students') {
      fetchEnrolledStudents();
    } else if (activeTab === 'summary') {
      fetchEnrolledStudents();
      fetchStudentStats();
    }
  }, [activeTab, levelCode]);

  const fetchDashboardData = async () => {
    try {
      // Fetch students
      const { data: studentsData } = await supabase
        .from('student_enrollment')
        .select('student_id')
        .eq('level_code', levelCode);

      // Fetch assignments
      const { data: assignmentsData } = await supabase
        .from('assignments')
        .select('*')
        .eq('level_code', levelCode);

      // Fetch grades
      const { data: gradesData } = await supabase
        .from('grades')
        .select('*')
        .in('assignment_id', assignmentsData?.map(a => a.id) || []);

      // Fetch attendance
      const { data: attendanceData } = await supabase
        .from('attendance')
        .select('*')
        .eq('level_code', levelCode)
        .gte('date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString());

      // Calculate stats
      const totalStudents = studentsData?.length || 0;
      const totalAssignments = assignmentsData?.length || 0;
      const pendingGrades = assignmentsData?.filter(a => 
        !gradesData?.some(g => g.assignment_id === a.id)
      ).length || 0;
      
      const attendanceRate = attendanceData?.length > 0 
        ? (attendanceData.filter(a => a.status === 'present').length / attendanceData.length) * 100
        : 0;

      setStats({
        totalStudents,
        totalAssignments,
        pendingGrades,
        attendanceRate: Math.round(attendanceRate),
        recentActivity: 0
      });

      // Fetch recent activity (simplified for now)
      setRecentActivity([
        { type: 'assignment', message: 'New assignment posted', time: '2 hours ago' },
        { type: 'grade', message: 'Grades updated for Quiz 1', time: '1 day ago' },
        { type: 'attendance', message: 'Attendance marked for today', time: '2 days ago' }
      ]);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const fetchEnrolledStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('student_enrollment')
        .select(`
          *,
          student:users (
            id,
            name,
            email,
            role,
            code,
            phone_number,
            parent1_number,
            parent2_number,
            created_at,
            updated_at
          )
        `)
        .eq('level_code', levelCode);

      if (error) throw error;
      setEnrolledStudents(data.map(item => item.student));
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchStudentStats = async () => {
    try {
      const stats = {};
      
      // Fetch grades for all students
      const { data: grades, error: gradesError } = await supabase
        .from('grades')
        .select(`
          *,
          assignment:assignments!inner(*),
          student:users(name)
        `)
        .eq('assignment.level_code', levelCode);

      if (gradesError) throw gradesError;

      // Fetch attendance for all students
      const { data: attendance, error: attendanceError } = await supabase
        .from('attendance')
        .select('*')
        .eq('level_code', levelCode);

      if (attendanceError) throw attendanceError;

      // Sort students alphabetically by name
      const sortedStudents = [...enrolledStudents].sort((a, b) => a.name.localeCompare(b.name));
      
      // Calculate stats for each student
      sortedStudents.forEach(student => {
        const studentGrades = grades.filter(grade => grade.student_id === student.id);
        const studentAttendance = attendance.filter(record => record.student_id === student.id);
        
        // Calculate grade stats
        const totalScore = studentGrades.reduce((sum, grade) => sum + (grade.score || 0), 0);
        const maxPossible = studentGrades.reduce((sum, grade) => sum + (grade.assignment?.max_score || 0), 0);
        const gradePercentage = maxPossible > 0 ? Math.round((totalScore / maxPossible) * 100) : 0;
        
        // Calculate attendance stats
        const attendanceStats = {
          present: 0,
          absent: 0,
          late: 0,
          excused: 0,
          total: studentAttendance.length
        };
        
        studentAttendance.forEach(record => {
          if (record.status) {
            attendanceStats[record.status] = (attendanceStats[record.status] || 0) + 1;
          }
        });
        
        // Calculate weighted attendance percentage (excused counts as 1 point like present)
        const weightedScore = attendanceStats.present + attendanceStats.excused + (attendanceStats.late * 0.5);
        const attendancePercentage = attendanceStats.total > 0 ? Math.round((weightedScore / attendanceStats.total) * 100) : 0;
        
        stats[student.id] = {
          name: student.name,
          gradePercentage,
          totalScore,
          maxPossible,
          attendancePercentage,
          attendanceStats
        };
      });
      
      setStudentStats(stats);
    } catch (err) {
      console.error('Error fetching student stats:', err);
    }
  };


  const handleStudentAdded = () => {
    fetchEnrolledStudents();
    fetchDashboardData(); // Update stats
  };

  const handleRemoveStudent = async (studentId) => {
    if (window.confirm('Are you sure you want to remove this student from the class?')) {
      try {
        await studentService.removeStudentFromLevel(studentId, levelCode);
        await fetchEnrolledStudents();
        await fetchDashboardData(); // Update stats
      } catch (err) {
        setError(err.message);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-fade-in">
          <div className="w-16 h-16 loading-spinner"></div>
          <div className="text-center">
            <p className="text-primary-600 font-semibold text-lg">Loading Dashboard...</p>
            <p className="text-neutral-500 text-sm mt-1">Preparing your teaching tools</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-neutral-50 flex items-center justify-center container-mobile">
        <div className="card p-8 max-w-md w-full text-center animate-slide-up">
          <div className="w-16 h-16 bg-error-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-neutral-900 mb-2">Access Denied</h2>
          <p className="text-neutral-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const renderStudentsTab = () => (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-neutral-900">Students</h2>
          <p className="text-xs text-neutral-500">{enrolledStudents.length} enrolled</p>
        </div>
        <button
          onClick={() => setShowAddStudentModal(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          + Add
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {enrolledStudents.length === 0 ? (
          <div className="bg-neutral-50 rounded-lg p-8 text-center">
            <p className="text-neutral-500 mb-4">No students enrolled</p>
            <button
              onClick={() => setShowAddStudentModal(true)}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Add First Student
            </button>
          </div>
        ) : (
          [...enrolledStudents].sort((a, b) => a.name.localeCompare(b.name)).map((student) => (
            <div key={student.id} className="bg-neutral-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-sm">
                      {student.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-neutral-900 truncate">{student.name}</p>
                    <p className="text-xs text-neutral-500 truncate">{student.email}</p>
                    {student.code && (
                      <p className="text-xs text-neutral-500 mt-1">Code: {student.code}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/transcript/${student.id}`)}
                    className="text-primary-600 text-sm font-medium"
                  >
                    Transcript
                  </button>
                  <button
                    onClick={() => {
                      setEditingStudent(student);
                      setShowEditStudentModal(true);
                    }}
                    className="text-primary-600 text-sm font-medium"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleRemoveStudent(student.id)}
                    className="text-red-600 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderSummaryTab = () => {
    const studentStatsArray = Object.values(studentStats).sort((a, b) => a.name.localeCompare(b.name));
    
    return (
      <div className="p-4 space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-blue-900">{enrolledStudents.length}</div>
            <div className="text-xs text-blue-700 mt-1">Students</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-green-900">
              {studentStatsArray.length > 0 ? Math.round(studentStatsArray.reduce((sum, s) => sum + s.gradePercentage, 0) / studentStatsArray.length) : 0}%
            </div>
            <div className="text-xs text-green-700 mt-1">Avg Grade</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-yellow-900">
              {studentStatsArray.length > 0 ? Math.round(studentStatsArray.reduce((sum, s) => sum + s.attendancePercentage, 0) / studentStatsArray.length) : 0}%
            </div>
            <div className="text-xs text-yellow-700 mt-1">Attendance</div>
          </div>
        </div>

        {/* Student Cards */}
        <div className="space-y-3">
          {studentStatsArray.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <p>No student data available</p>
            </div>
          ) : (
            studentStatsArray.map((student) => {
              const getStatusText = (gradePercentage, attendancePercentage) => {
                if (gradePercentage >= 80 && attendancePercentage >= 80) return 'Excellent';
                if (gradePercentage >= 60 && attendancePercentage >= 60) return 'Good';
                return 'Needs Attention';
              };

              return (
                <div key={student.id} className="bg-neutral-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">{student.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-neutral-900">{student.name}</p>
                        {student.code && <p className="text-xs text-neutral-500">{student.code}</p>}
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      student.gradePercentage >= 80 && student.attendancePercentage >= 80 ? 'bg-green-100 text-green-700' :
                      student.gradePercentage >= 60 && student.attendancePercentage >= 60 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {getStatusText(student.gradePercentage, student.attendancePercentage)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-xs text-neutral-500">Grade</p>
                      <p className="font-bold text-sm text-neutral-900">{student.gradePercentage}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-500">Attendance</p>
                      <p className="font-bold text-sm text-neutral-900">{student.attendancePercentage}%</p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'grades', label: 'Grades', icon: '📊' },
    { id: 'attendance', label: 'Attendance', icon: '📅' },
    { id: 'assignments', label: 'Assignments', icon: '📝' },
    { id: 'students', label: 'Students', icon: '👥' },
    { id: 'announcements', label: 'Updates', icon: '📢' },
    { id: 'summary', label: 'Summary', icon: '📈' }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 pb-20">
      {/* Simple Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-neutral-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-bold text-neutral-900">
                  {academicClass?.name || classData?.name || `Level ${levelCode}`}
                </h1>
                <p className="text-xs text-neutral-500">
                  {academicClass?.term?.name ? `${academicClass.term.name} · ` : ''}
                  {academicClass?.subjects?.map((subject) => subject.name).join(' · ')
                    || classData?.period
                    || 'Period'}
                </p>
              </div>
            </div>
          </div>

          {/* Scrollable Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-600'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-4">
        <div className="bg-white rounded-xl shadow-sm">
          {activeTab === 'summary' && renderSummaryTab()}
          {activeTab === 'announcements' && <AnnouncementsTab levelCode={levelCode} teacherId={userData.id} />}
          {activeTab === 'assignments' && <AssignmentsTab levelCode={levelCode} teacherId={userData.id} />}
          {activeTab === 'grades' && <GradesTab levelCode={levelCode} teacherId={userData.id} />}
          {activeTab === 'attendance' && <AttendanceTab levelCode={levelCode} teacherId={userData.id} />}
          {activeTab === 'students' && renderStudentsTab()}
        </div>
      </main>

      {/* Add Student Modal */}
      <AddStudentModal
        isOpen={showAddStudentModal}
        onClose={() => setShowAddStudentModal(false)}
        levelCode={levelCode}
        onStudentAdded={handleStudentAdded}
      />

      {/* Edit Student Modal */}
      <EditStudentModal
        isOpen={showEditStudentModal}
        onClose={() => {
          setShowEditStudentModal(false);
          setEditingStudent(null);
        }}
        student={editingStudent}
        onStudentUpdated={handleStudentAdded}
      />
    </div>
  );
}
